import moment from 'moment';
import {
  getGridSetting,
  setGridSetting
} from './utils/storage';

class FilterCriteria {
  /**
   * In addition to filterable columns, add synthetic filters
   * which are constructed from values in the row.
   * Each synthetic filter will need a function to evaluate it.
   * Also we manage the order to display fields in the filter dropdown here.
   * @param title
   * @param columns
   */
  constructor(title, columns) {
    this._title = title;
    this._columns = columns;
    this._fields  = columns.filter(c => c && c.filterable).map(c => {
      const newC = Object.assign({}, c);
      if (newC.id === 'start' || newC.id === 'end') {
        newC.displayOrder = 2;
      } else {
        newC.displayOrder = 3;
      }
      return new Criterion(newC, this);
    });
    this._fields.push(new Criterion({ id: 'synth1', label: 'Start Month', displayOrder: 1 }, this));
    this._fields.push(new Criterion({ id: 'synth2', label: 'End Month', displayOrder: 1 }, this));
    this._fields = this._fields.sort((a, b) => a.displayOrder - b.displayOrder);
  }

  get title() {
    return this._title;
  }

  clone() {
    const cloned = new FilterCriteria(this._title, this._columns);
    cloned.fields.forEach((cf) => {
      const f = this.find(cf.id);
      cf.value = f.value;
    });
    return cloned;
  }

  get observer() {
    return this._observer;
  }

  set observer(observer) {
    this.notificationsEnabled = true;
    this._observer = observer;
  }

  get notificationsEnabled() {
    return this._notificationsEnabled;
  }

  set notificationsEnabled(v) {
    this._notificationsEnabled = v;
  }

  notifyObserver() {
    if (this.notificationsEnabled && this.observer) {
      this.observer(this);
    }
  }

  get fields() {
    return this._fields;
  }

  get activeFields() {
    return this._fields.filter(c => c.isActive);
  }

  fieldChanged(f) {
    this.notifyObserver();
  }

  get isEmpty() {
    return !this._fields.some(f => !f.isEmpty);
  }

  assign(other) {
    const prevNotf = this.notificationsEnabled;
    this.notificationsEnabled = false;
    other.fields.forEach((otherField) => {
      const f = this.find(otherField.id);
      if (f) {
        f.value = otherField.value;
      }
    });
    this.notificationsEnabled = prevNotf;
    this.notifyObserver();
  }

  clear() {
    const prevNotf = this.notificationsEnabled;
    this.notificationsEnabled = false;
    this._fields.forEach(f => f.clear());
    this.notificationsEnabled = prevNotf;
    this.notifyObserver();
  }

  find(id) {
    return this.fields.find(f => f.id === id);
  }

  matches(row) {
    return this.activeFields.every(f => f.matches(row));
  }

  save() {
    const serialized = this.fields.map(f => ({ id: f.id, value: f.value }));
    setGridSetting(this.title, 'filterCriteria', serialized);
  }

  load() {
    const prevNot = this.notificationsEnabled;
    this.notificationsEnabled = false;
    const serialized = getGridSetting(this.title, 'filterCriteria') || [];
    serialized.forEach((s) => {
      const f = this.find(s.id);
      if (f) f.value = s.value;
    });
    this.notificationsEnabled = prevNot;
  }
}

class Criterion {
  constructor(col, owner) {
    this._owner = owner;
    this._col = col;
    this._value = null;
  }

  get id() {
    return this._col.id;
  }

  get isDate() {
    return this._col.date;
  }

  get isSynth() {
    return this._col.id.slice(0, 5) === 'synth';
  }

  get dateFmt() {
    return this._col.date;
  }

  get label() {
    return this._col.label;
  }

  get value() {
    return this._value;
  }

  get displayOrder() {
    return this._col.displayOrder;
  }

  set value(v) {
    if (!this.validate(v)) return;
    if (moment.isMoment(v)) {
      v = v.toISOString();
    }
    this._value = v;
    this._owner.fieldChanged(this);
  }

  get displayValue() {
    if (this.dateFmt && !this.isEmpty) {
      return this.formatDate(this.value);
    }
    if (this._col.id.slice(0, 5) === 'synth') {
      return this.value ? moment(this.value).format("MMMM/yyyy") : "";
    }
    return this.value || ''; // components don't like null/undefined values
  }

  get isEmpty() {
    return !this._value;
  }

  get isActive() {
    return !this.isEmpty;
  }

  validate(v) {
    if (!this.isDate) return true;
    try {
      this.formatDate(v);
    } catch(e) {
      return false;
    }
    return true;

  }

  formatDate(str) {
    return moment(str).format(this.dateFmt);
  }

  clear() {
    this.value = null;
  }

  // look for start or end date matching month
  synth1(row) {    
    return !this.value  ? true : moment(row.start).startOf("month").isSame(moment(this.value).startOf("month"))
    
  }

  synth2(row) {
    return !this.value  ? true : moment(row.end).startOf("month").isSame(moment(this.value).startOf("month"))
  }

  matches(row) {
    if (this.isEmpty) return true;
    if (this._col.id.slice(0, 5) === 'synth') {
      if (this._col.id === 'synth1') {
        return this.synth1(row);
      }
      if (this._col.id === 'synth2') {
        return this.synth2(row);
      }
    }
    const colVal = row[this.id];
    if (this.dateFmt) {
      return colVal && (colVal.toString().slice(0, 10)).includes(this.value.slice(0, 10));
    }
    return colVal && (colVal.toString().toLowerCase()).includes(this.value.toLowerCase());
  }

}


export default FilterCriteria;
